import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getForm, createForm, updateForm, publishForm, unpublishForm,
  getFormSubmissions, updateSubmissionStatus
} from '../utils/api';
import { formatDate, formatDateTime } from '../utils/helpers';
import {
  ArrowLeft, Save, Globe, Lock, Copy, Trash2, Plus, GripVertical,
  Type, AlignLeft, ChevronDown, Calendar, CheckSquare, ListChecks, Hash,
  Phone, Mail, MapPin, FileText, Star, Pen, Settings, Eye, ClipboardList,
  Link as LinkIcon, X, ExternalLink
} from 'lucide-react';
import toast from 'react-hot-toast';

// ═══════════════════════════════════════════
// FIELD PALETTE DEFINITIONS
// ═══════════════════════════════════════════
const FIELD_CATEGORIES = [
  {
    label: 'CONTACT FIELDS',
    fields: [
      { type: 'phone', label: 'Phone Number', icon: Phone },
      { type: 'email_field', label: 'Email', icon: Mail },
      { type: 'address', label: 'Address', icon: MapPin },
      { type: 'date', label: 'Date of Birth', icon: Calendar },
      { type: 'text', label: 'SSN', icon: Hash, defaultLabel: 'Social Security Number' },
      { type: 'dropdown', label: 'Language', icon: ChevronDown, defaultOptions: ['English','Spanish','Vietnamese','Chinese','Other'] },
      { type: 'text', label: 'Custom Field', icon: Pen, defaultLabel: '' },
    ],
  },
  {
    label: 'CASE FIELDS',
    fields: [
      { type: 'date', label: 'Date of Incident', icon: Calendar, defaultLabel: 'Date of Incident' },
      { type: 'paragraph', label: 'Injury Description', icon: AlignLeft, defaultLabel: 'Describe your injury' },
      { type: 'text', label: 'Body Parts Injured', icon: Type, defaultLabel: 'Body Parts Injured' },
      { type: 'text', label: 'Employer', icon: Type, defaultLabel: 'Employer Name' },
      { type: 'dropdown', label: 'Case Type', icon: ChevronDown, defaultLabel: 'Case Type', defaultOptions: ['Workers Compensation','Personal Injury','Slip and Fall','Motor Vehicle Accident','Other'] },
      { type: 'text', label: 'Referred By', icon: Type, defaultLabel: 'Referred By' },
      { type: 'dropdown', label: 'Source', icon: ChevronDown, defaultLabel: 'How did you hear about us?', defaultOptions: ['Internet Search','Social Media','Referral','Advertisement','Walk-In','Other'] },
    ],
  },
  {
    label: 'BASIC FIELDS',
    fields: [
      { type: 'text', label: 'Short Text', icon: Type },
      { type: 'paragraph', label: 'Paragraph', icon: AlignLeft },
      { type: 'checkbox', label: 'Single Checkbox', icon: CheckSquare },
      { type: 'checkboxes', label: 'Checkboxes', icon: ListChecks },
      { type: 'dropdown', label: 'Dropdown', icon: ChevronDown },
      { type: 'date', label: 'Date', icon: Calendar },
      { type: 'number', label: 'Number', icon: Hash },
      { type: 'file', label: 'File Upload', icon: FileText },
      { type: 'section_heading', label: 'Section Heading', icon: Star },
      { type: 'signature', label: 'Signature', icon: Pen },
    ],
  },
];

const genId = () => 'f_' + Math.random().toString(36).substring(2, 9);

export default function FormBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('fields'); // fields, submissions, settings
  const [form, setForm] = useState({
    name: 'Untitled Form',
    description: '',
    category: 'Intake',
    collect_name: true,
    collect_email: true,
    collect_phone: true,
    fields: [],
    status: 'draft',
    publish_token: '',
    confirmation_message: 'Thank you! Your submission has been received.',
    notify_email: '',
    submission_count: 0,
  });
  const [formId, setFormId] = useState(id === 'new' ? null : id);
  const [selectedField, setSelectedField] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Load existing form
  useEffect(() => {
    if (!isNew && id) {
      (async () => {
        try {
          const data = await getForm(id);
          setForm({
            name: data.name || 'Untitled Form',
            description: data.description || '',
            category: data.category || 'Intake',
            collect_name: data.collect_name ?? true,
            collect_email: data.collect_email ?? true,
            collect_phone: data.collect_phone ?? true,
            fields: data.fields || [],
            status: data.status || 'draft',
            publish_token: data.publish_token || '',
            confirmation_message: data.confirmation_message || '',
            notify_email: data.notify_email || '',
            submission_count: data.submission_count || 0,
          });
          setFormId(data._id);
        } catch { toast.error('Failed to load form'); navigate('/templates'); }
        finally { setLoading(false); }
      })();
    }
  }, [id]);

  // Load submissions when tab changes
  useEffect(() => {
    if (tab === 'submissions' && formId) {
      (async () => {
        setLoadingSubs(true);
        try { const subs = await getFormSubmissions(formId); setSubmissions(Array.isArray(subs) ? subs : []); }
        catch { setSubmissions([]); }
        finally { setLoadingSubs(false); }
      })();
    }
  }, [tab, formId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (formId) {
        await updateForm(formId, form);
        toast.success('Form saved');
      } else {
        const created = await createForm(form);
        setFormId(created._id);
        toast.success('Form created');
        // Update URL without full navigation
        window.history.replaceState(null, '', `/form-builder/${created._id}`);
      }
      setDirty(false);
    } catch { toast.error('Failed to save form'); }
    finally { setSaving(false); }
  };

  const handlePublish = async () => {
    if (!formId) {
      // Save first
      setSaving(true);
      try {
        const created = await createForm(form);
        setFormId(created._id);
        window.history.replaceState(null, '', `/form-builder/${created._id}`);
        const result = await publishForm(created._id);
        setForm(prev => ({ ...prev, status: 'published', publish_token: result.form.publish_token }));
        toast.success('Form published! Link is ready.');
      } catch { toast.error('Failed to publish'); }
      finally { setSaving(false); }
      return;
    }
    try {
      // Save then publish
      await updateForm(formId, form);
      const result = await publishForm(formId);
      setForm(prev => ({ ...prev, status: 'published', publish_token: result.form.publish_token }));
      setDirty(false);
      toast.success('Form published!');
    } catch { toast.error('Failed to publish'); }
  };

  const handleUnpublish = async () => {
    if (!formId) return;
    try {
      await unpublishForm(formId);
      setForm(prev => ({ ...prev, status: 'draft' }));
      toast.success('Form unpublished');
    } catch { toast.error('Failed to unpublish'); }
  };

  const updateField = (fieldId, updates) => {
    setForm(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.id === fieldId ? { ...f, ...updates } : f),
    }));
    setDirty(true);
  };

  const addField = (fieldDef) => {
    const newField = {
      id: genId(),
      type: fieldDef.type,
      label: fieldDef.defaultLabel || fieldDef.label || '',
      placeholder: '',
      required: false,
      options: fieldDef.defaultOptions || (fieldDef.type === 'dropdown' ? ['Option 1', 'Option 2'] : fieldDef.type === 'checkboxes' ? ['Option 1', 'Option 2'] : []),
      width: 'full',
      order: form.fields.length,
    };
    setForm(prev => ({ ...prev, fields: [...prev.fields, newField] }));
    setSelectedField(newField.id);
    setDirty(true);
  };

  const removeField = (fieldId) => {
    setForm(prev => ({ ...prev, fields: prev.fields.filter(f => f.id !== fieldId) }));
    if (selectedField === fieldId) setSelectedField(null);
    setDirty(true);
  };

  const moveField = (fieldId, direction) => {
    setForm(prev => {
      const idx = prev.fields.findIndex(f => f.id === fieldId);
      if (idx < 0) return prev;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.fields.length) return prev;
      const fields = [...prev.fields];
      [fields[idx], fields[newIdx]] = [fields[newIdx], fields[idx]];
      return { ...prev, fields };
    });
    setDirty(true);
  };

  const publicUrl = form.publish_token ? `${window.location.origin}/forms/submit/${form.publish_token}` : '';

  const copyLink = () => {
    navigator.clipboard?.writeText(publicUrl);
    toast.success('Link copied to clipboard');
  };

  // Field icon map
  const fieldIcon = (type) => {
    const map = { text: Type, paragraph: AlignLeft, dropdown: ChevronDown, date: Calendar, checkbox: CheckSquare, checkboxes: ListChecks, number: Hash, phone: Phone, email_field: Mail, address: MapPin, file: FileText, section_heading: Star, signature: Pen };
    const Icon = map[type] || Type;
    return <Icon size={14} />;
  };

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  // ═══════════════════════════════════════════
  // SUBMISSIONS TAB
  // ═══════════════════════════════════════════
  const SubmissionsView = () => (
    <div style={{ padding: '24px', overflow: 'auto', flex: 1 }}>
      {loadingSubs ? (
        <div className="loading-spinner"><div className="spinner" /></div>
      ) : submissions.length === 0 ? (
        <div className="empty-state" style={{ padding: '60px 20px' }}>
          <ClipboardList />
          <h3>No submissions yet</h3>
          <p>{form.status === 'published' ? 'Share your form link to start collecting responses.' : 'Publish your form to start accepting submissions.'}</p>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Submitted</th>
                <th>Status</th>
                {form.fields.slice(0, 3).map(f => <th key={f.id}>{f.label || f.type}</th>)}
              </tr>
            </thead>
            <tbody>
              {submissions.map(s => (
                <tr key={s._id}>
                  <td style={{ fontWeight: 600 }}>{s.first_name} {s.last_name}</td>
                  <td style={{ fontSize: '0.82rem' }}>{s.email || '—'}</td>
                  <td style={{ fontSize: '0.82rem' }}>{s.phone || '—'}</td>
                  <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{formatDateTime(s.createdAt)}</td>
                  <td>
                    <select className="form-select" style={{ padding: '3px 8px', fontSize: '0.75rem', width: 'auto' }}
                      value={s.status} onChange={async (e) => {
                        try { await updateSubmissionStatus(s._id, e.target.value); setSubmissions(prev => prev.map(x => x._id === s._id ? { ...x, status: e.target.value } : x)); } catch {}
                      }}>
                      <option value="new">New</option>
                      <option value="reviewed">Reviewed</option>
                      <option value="archived">Archived</option>
                    </select>
                  </td>
                  {form.fields.slice(0, 3).map(f => (
                    <td key={f.id} style={{ fontSize: '0.82rem', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {String(s.responses?.[f.id] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ═══════════════════════════════════════════
  // SETTINGS TAB
  // ═══════════════════════════════════════════
  const SettingsView = () => (
    <div style={{ padding: '24px', maxWidth: '600px', margin: '0 auto' }}>
      <div className="card" style={{ padding: '20px' }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', marginBottom: '16px' }}>Form Settings</h3>
        <div className="form-group">
          <label className="form-label">Confirmation Message</label>
          <textarea className="form-textarea" rows={3} value={form.confirmation_message}
            onChange={(e) => { setForm(prev => ({ ...prev, confirmation_message: e.target.value })); setDirty(true); }}
            placeholder="Message shown after submission" />
        </div>
        <div className="form-group">
          <label className="form-label">Notification Email</label>
          <input className="form-input" type="email" value={form.notify_email}
            onChange={(e) => { setForm(prev => ({ ...prev, notify_email: e.target.value })); setDirty(true); }}
            placeholder="Receive email on each submission" />
        </div>
        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-muted)', marginTop: '16px', marginBottom: '8px' }}>Always Collected</div>
        {[
          { key: 'collect_name', label: 'First & Last Name' },
          { key: 'collect_email', label: 'Email Address' },
          { key: 'collect_phone', label: 'Phone Number' },
        ].map(({ key, label }) => (
          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', fontSize: '0.88rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={form[key]} style={{ accentColor: 'var(--zeus-gold)' }}
              onChange={(e) => { setForm(prev => ({ ...prev, [key]: e.target.checked })); setDirty(true); }} />
            {label}
          </label>
        ))}
      </div>
    </div>
  );

  // ═══════════════════════════════════════════
  // SELECTED FIELD EDITOR
  // ═══════════════════════════════════════════
  const selField = form.fields.find(f => f.id === selectedField);

  const FieldEditor = () => {
    if (!selField) return null;
    return (
      <div style={{ padding: '14px', borderTop: '1px solid var(--zeus-border)', background: 'var(--zeus-navy-light)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--zeus-gold)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Edit Field</div>
          <button onClick={() => setSelectedField(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px' }}><X size={14} /></button>
        </div>
        <div className="form-group" style={{ marginBottom: '8px' }}>
          <label style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Label</label>
          <input className="form-input" style={{ padding: '5px 8px', fontSize: '0.82rem' }} value={selField.label}
            onChange={(e) => updateField(selField.id, { label: e.target.value })} />
        </div>
        {selField.type !== 'section_heading' && selField.type !== 'checkbox' && (
          <div className="form-group" style={{ marginBottom: '8px' }}>
            <label style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Placeholder</label>
            <input className="form-input" style={{ padding: '5px 8px', fontSize: '0.82rem' }} value={selField.placeholder || ''}
              onChange={(e) => updateField(selField.id, { placeholder: e.target.value })} />
          </div>
        )}
        {(selField.type === 'dropdown' || selField.type === 'checkboxes') && (
          <div className="form-group" style={{ marginBottom: '8px' }}>
            <label style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Options (one per line)</label>
            <textarea className="form-textarea" rows={4} style={{ fontSize: '0.8rem', padding: '5px 8px' }}
              value={(selField.options || []).join('\n')}
              onChange={(e) => updateField(selField.id, { options: e.target.value.split('\n') })} />
          </div>
        )}
        {selField.type !== 'section_heading' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', cursor: 'pointer', marginBottom: '6px' }}>
            <input type="checkbox" checked={selField.required} style={{ accentColor: 'var(--zeus-gold)' }}
              onChange={(e) => updateField(selField.id, { required: e.target.checked })} />
            Required
          </label>
        )}
        <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.72rem' }} onClick={() => moveField(selField.id, -1)}>↑ Move Up</button>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.72rem' }} onClick={() => moveField(selField.id, 1)}>↓ Move Down</button>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.72rem', color: 'var(--status-red)', marginLeft: 'auto' }} onClick={() => removeField(selField.id)}><Trash2 size={12} /> Remove</button>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* ── TOP BAR ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 20px', borderBottom: '1px solid var(--zeus-border)', background: 'var(--zeus-navy)', flexShrink: 0 }}>
        <Link to="/templates" className="btn btn-ghost btn-sm"><ArrowLeft size={16} /> Back</Link>
        <div style={{ flex: 1 }} />

        {/* Status badge */}
        <span style={{
          padding: '3px 10px', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 700,
          background: form.status === 'published' ? 'rgba(34,197,94,0.12)' : 'rgba(100,116,139,0.12)',
          color: form.status === 'published' ? '#22c55e' : '#64748b',
          display: 'flex', alignItems: 'center', gap: '4px',
        }}>
          {form.status === 'published' ? <><Globe size={12} /> Published</> : <><Lock size={12} /> Draft</>}
        </span>

        {form.status === 'published' && publicUrl && (
          <button className="btn btn-ghost btn-sm" onClick={copyLink} title="Copy link" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <LinkIcon size={13} /> Copy Link
          </button>
        )}
        {form.status === 'published' && publicUrl && (
          <a href={`/forms/submit/${form.publish_token}`} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <ExternalLink size={13} /> Preview
          </a>
        )}

        <button className="btn btn-secondary btn-sm" onClick={handleSave} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving...' : 'Save'}
        </button>
        {form.status === 'published' ? (
          <button className="btn btn-ghost btn-sm" onClick={handleUnpublish} style={{ color: '#f97316' }}>
            <Lock size={14} /> Unpublish
          </button>
        ) : (
          <button className="btn btn-primary btn-sm" onClick={handlePublish}>
            <Globe size={14} /> Publish
          </button>
        )}
      </div>

      {/* ── TAB BAR ── */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid var(--zeus-border)', background: 'var(--zeus-navy)', flexShrink: 0, padding: '0 20px' }}>
        {[
          { key: 'fields', label: 'Fields', icon: ListChecks },
          { key: 'submissions', label: `Submissions${form.submission_count ? ` (${form.submission_count})` : ''}`, icon: ClipboardList },
          { key: 'settings', label: 'Settings', icon: Settings },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '10px 18px', fontSize: '0.85rem', fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? 'var(--zeus-gold)' : 'var(--text-muted)',
              background: 'transparent', border: 'none', cursor: 'pointer',
              borderBottom: tab === t.key ? '2px solid var(--zeus-gold)' : '2px solid transparent',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {/* ── MAIN CONTENT ── */}
      {tab === 'submissions' ? <SubmissionsView /> : tab === 'settings' ? <SettingsView /> : (
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* LEFT: Form Canvas */}
          <div style={{ flex: 1, overflow: 'auto', padding: '30px 20px', background: 'var(--zeus-black)' }}>
            <div style={{ maxWidth: '680px', margin: '0 auto' }}>
              {/* Form title & description */}
              <div style={{ background: 'var(--zeus-navy)', border: '1px solid var(--zeus-border)', borderRadius: 'var(--radius-lg)', padding: '28px 32px', marginBottom: '16px' }}>
                <input
                  value={form.name}
                  onChange={(e) => { setForm(prev => ({ ...prev, name: e.target.value })); setDirty(true); }}
                  placeholder="Untitled Form"
                  style={{
                    background: 'none', border: 'none', outline: 'none', width: '100%',
                    fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 400,
                    color: 'var(--text-primary)', marginBottom: '4px',
                  }}
                />
                <input
                  value={form.description}
                  onChange={(e) => { setForm(prev => ({ ...prev, description: e.target.value })); setDirty(true); }}
                  placeholder="Click to add a description"
                  style={{
                    background: 'none', border: 'none', outline: 'none', width: '100%',
                    fontSize: '0.88rem', color: 'var(--text-muted)',
                  }}
                />

                {/* Built-in fields notice */}
                <div style={{
                  marginTop: '16px', padding: '10px 14px', borderRadius: 'var(--radius-sm)',
                  background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)',
                  fontSize: '0.82rem', color: '#60a5fa',
                }}>
                  <strong>First name, last name{form.collect_email ? ', email' : ''}{form.collect_phone ? ', and phone' : ''}</strong> are always collected by every form.
                </div>

                {/* FIELDS LIST */}
                {form.fields.length === 0 ? (
                  <div style={{
                    marginTop: '24px', padding: '40px 20px', textAlign: 'center',
                    border: '2px dashed var(--zeus-border)', borderRadius: 'var(--radius-md)',
                    color: 'var(--text-muted)',
                  }}>
                    <Plus size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                    <div style={{ fontSize: '0.95rem' }}>Click a field from the panel to get started!</div>
                  </div>
                ) : (
                  <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {form.fields.map((field, idx) => (
                      <div
                        key={field.id}
                        onClick={() => setSelectedField(field.id === selectedField ? null : field.id)}
                        style={{
                          padding: '12px 14px', borderRadius: 'var(--radius-sm)',
                          border: `1px solid ${field.id === selectedField ? 'var(--zeus-gold)' : 'var(--zeus-border)'}`,
                          background: field.id === selectedField ? 'rgba(212,168,83,0.06)' : 'var(--zeus-navy-light)',
                          cursor: 'pointer', transition: 'border-color 0.15s',
                          display: 'flex', alignItems: 'center', gap: '10px',
                        }}
                      >
                        <GripVertical size={14} style={{ color: 'var(--text-muted)', flexShrink: 0, opacity: 0.4 }} />
                        <span style={{ color: 'var(--zeus-gold)', flexShrink: 0 }}>{fieldIcon(field.type)}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.88rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                            {field.label || <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>Untitled field</span>}
                            {field.required && <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>}
                          </div>
                          {field.type === 'section_heading' ? null : (
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '1px' }}>
                              {field.type === 'dropdown' ? `Dropdown · ${(field.options || []).length} options` :
                               field.type === 'checkboxes' ? `Checkboxes · ${(field.options || []).length} options` :
                               field.type.replace('_', ' ')}
                            </div>
                          )}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); removeField(field.id); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px', opacity: 0.6 }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Field Palette + Editor */}
          <div style={{ width: '320px', borderLeft: '1px solid var(--zeus-border)', background: 'var(--zeus-navy)', display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
            {/* Field editor when a field is selected */}
            <FieldEditor />

            {/* Palette header */}
            <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--zeus-border)', background: 'var(--zeus-navy-light)' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center' }}>ADD A FIELD</div>
            </div>

            {/* Field categories */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
              {FIELD_CATEGORIES.map(cat => (
                <div key={cat.label}>
                  <div style={{
                    fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)',
                    padding: '10px 14px 4px', letterSpacing: '0.06em',
                  }}>{cat.label}</div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px', padding: '4px 10px' }}>
                    {cat.fields.map((f, i) => {
                      const Icon = f.icon;
                      return (
                        <button
                          key={`${cat.label}-${i}`}
                          onClick={() => addField(f)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '5px',
                            padding: '6px 6px', fontSize: '0.74rem', color: 'var(--text-secondary)',
                            background: 'transparent', border: '1px solid transparent',
                            borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                            textAlign: 'left', transition: 'all 0.15s',
                            minWidth: 0,
                          }}
                          onMouseOver={(e) => { e.currentTarget.style.background = 'var(--zeus-gold-dim)'; e.currentTarget.style.borderColor = 'var(--zeus-border)'; }}
                          onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}
                        >
                          <Icon size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{f.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
